/**
 * TextInput — shared types, Zod schema, styling constants, and helpers.
 *
 * Platform-specific implementations live in TextInput.native.tsx and TextInput.web.tsx.
 * Callbacks (onChangeText, onBlur, etc.) are platform-specific and not part of the catalog schema.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { typography } from '../../elements/typography';
import { spacing } from '../../elements/spacing';
import { radius } from '../../elements/radius';
import type { TypeStyle } from '../../elements/typography';

// ---------------------------------------------------------------------------
// Zod schema (catalog-safe: data/variant props only, no callbacks)
// ---------------------------------------------------------------------------

// `value` and `defaultValue` are mutually exclusive on the same instance
// (React rule). The base schema accepts either for catalog-safety;
// `textInputPropsStrictSchema` below refines the mutex for runtime callers.
export const textInputPropsSchema = z.object({
  label: z.string(),
  value: z.string().optional(),
  defaultValue: z.string().optional(),
  placeholder: z.string().optional(),
  helperText: z.string().optional(),
  errorText: z.string().optional(),
  required: z.boolean().default(false),
  disabled: z.boolean().default(false),
  /**
   * Placeholder-only field: suppress the floating in-field label so the box
   * shows only its placeholder. Use when a separate label is rendered above the
   * field (the RN placeholder + `accessibilityLabel` still carry the field name).
   */
  hideLabel: z.boolean().default(false),
  autoFocus: z.boolean().default(false),
  autoComplete: z.enum(['name']).optional(),
  /** Conversational name hint; native maps it to each platform's supported purpose. */
  autofillPurpose: z.enum(['preferred-name']).optional(),
  /** Multi-line entry (e.g. a note); the field grows to `numberOfLines`. */
  multiline: z.boolean().default(false),
  numberOfLines: z.number().int().positive().optional(),
  testID: z.string().optional(),
  accessibilityLabel: z.string().optional(),
});

export type TextInputProps = z.infer<typeof textInputPropsSchema>;
export type TextInputInputProps = z.input<typeof textInputPropsSchema>;

// Kept separate from the base schema so `catalog.jsonSchema()` (ADR-011 Rule 5)
// keeps serialising cleanly — refinements don't survive the round trip reliably.
export const textInputPropsStrictSchema = textInputPropsSchema.refine(
  ({ value, defaultValue }) => !(value !== undefined && defaultValue !== undefined),
  { message: 'TextInput: value and defaultValue must not both be set' },
);

export function normaliseTextInputProps(props: TextInputInputProps): TextInputProps {
  return {
    ...props,
    value: props.value ?? (props.defaultValue !== undefined ? undefined : ''),
    required: props.required ?? false,
    disabled: props.disabled ?? false,
    hideLabel: props.hideLabel ?? false,
    autoFocus: props.autoFocus ?? false,
    multiline: props.multiline ?? false,
  };
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

// Matches the select/option card radius (radius.xl) so text fields and option
// cards share the same corner rounding when stacked in a form.
export const TEXT_INPUT_BORDER_RADIUS = radius.xl;
export const TEXT_INPUT_BORDER_WIDTH = 1;
export const TEXT_INPUT_HORIZONTAL_PADDING = spacing.m;
// Vertical padding in the minimized (label-floated) state.
export const TEXT_INPUT_VERTICAL_PADDING = spacing.sm;
// Fixed field height from the Figma Textfield (single-line).
export const TEXT_INPUT_HEIGHT = 70;
// Multiline fields grow with their visible line count instead of the fixed
// single-line height: value-line height per visible line (b2Regular's 22px),
// plus the minimized-state vertical padding.
export const TEXT_INPUT_MULTILINE_LINE_HEIGHT = 22;
export const TEXT_INPUT_MULTILINE_DEFAULT_LINES = 3;
// Gap between the minimized top label and the value line.
export const TEXT_INPUT_LABEL_VALUE_GAP = spacing.xxs;
export const TEXT_INPUT_HELPER_GAP = spacing.xs;
// Leading icon gap for the error row beneath the field.
export const TEXT_INPUT_ERROR_ICON_GAP = spacing.s;
export const TEXT_INPUT_ERROR_ICON_SIZE = 14;

// Typography
// Minimized top label + error message — `c2Regular` is the design-system token
// equal to the Figma `c2-medium` variable (12px / weight 500).
export const TEXT_INPUT_MINIMIZED_LABEL_TYPOGRAPHY: TypeStyle = typography.c2Regular;
// The label rendered as a centered placeholder while empty + unfocused.
export const TEXT_INPUT_PLACEHOLDER_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const TEXT_INPUT_VALUE_TYPOGRAPHY: TypeStyle = typography.b2Regular;
export const TEXT_INPUT_HELPER_TYPOGRAPHY: TypeStyle = typography.c2Regular;

/**
 * The field minimizes its label (floats it to the top and reveals the value
 * line) when it is focused OR already holds a value; otherwise the label sits
 * centered as a placeholder.
 */
export function isTextInputMinimized(value: string | undefined, focused: boolean): boolean {
  return focused || (value !== undefined && value !== '');
}

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export interface TextInputColors {
  border: string;
  label: string;
  value: string;
  placeholder: string;
  helper: string;
  background: string;
}

export function textInputColorsFor(
  disabled: boolean,
  hasError: boolean,
  focused = false,
): TextInputColors {
  // Border: error wins, then the active (focused) accent, else the resting
  // divider. A disabled field keeps the resting divider on its muted fill.
  const border = hasError
    ? colors.button.critical
    : !disabled && focused
      ? colors.stroke.primary
      : colors.stroke.divider;

  if (disabled) {
    return {
      border,
      label: colors.text.disabled,
      value: colors.text.disabled,
      placeholder: colors.text.disabled,
      helper: colors.text.disabled,
      background: colors.background.primary,
    };
  }

  return {
    border,
    // The floated (minimized) label sits in the tertiary caption colour.
    label: colors.text.tertiary,
    value: colors.text.primary,
    placeholder: colors.text.disabled,
    // Error message text stays in the primary (near-black) colour; the red
    // accent is carried by the field border and the alert icon, not the copy.
    helper: hasError ? colors.text.primary : colors.text.secondary,
    background: colors.background.container,
  };
}

// ---------------------------------------------------------------------------
// Accessibility helpers
// ---------------------------------------------------------------------------

export function textInputAccessibleLabel(label: string, accessibilityLabel?: string): string {
  return accessibilityLabel ?? label;
}
